import React from 'react'
import { Button, Result, Table, Tag } from 'antd'
import { useDispatch } from 'react-redux'
import { useFetchUsersQuery } from '../../../../../store/apis/user.api'
import { EditOutlined, MinusCircleOutlined, ClockCircleOutlined, CheckCircleOutlined } from '@ant-design/icons'
import UserRoles, { getRoleName, getTypeName, UserTypes } from '../../../../../constants/UserRoles'
import UserStatus, { getUserStatusText } from '../../../../../constants/UserStatus'
import useQueryParam from '../../../../../hooks/useQueryParam'
import { USER_SEARCH_PARAM } from '../../../../../constants/query-params'
import { userActions } from '../../../../../store/slices/user.slice'
import { useFetchOrganizationsQuery } from '../../../../../store/apis/organization.api'
import useAuthorization from '../../../../../components/authorization/useAuthorization'
import { useIsSenseUserOrImpersonated } from '../../../../../hooks/useIsSenseUserOrImpersonated'
import { getVisibleRoles, getVisibleRoleFilterOptions } from '../../../../../utils/rolePermissions.helper'
import { isNil } from 'lodash'

const UsersTable = () => {
    const dispatch = useDispatch()
    const searchParam = useQueryParam(USER_SEARCH_PARAM)
    
    const isSenseUserOrImpersonated = useIsSenseUserOrImpersonated()

    const { isAuthorized: isSystemAdmin } = useAuthorization([UserRoles.ADMIN], [UserTypes.SYSTEM_ADMIN])
    const { data, isFetching, error, refetch } = useFetchUsersQuery({ organizationOnly: !isSystemAdmin })
    const { data: organizations, isFetching: isFetchingOrganizations } = useFetchOrganizationsQuery(null, { skip: !isSystemAdmin })

    // Get visible role options for filters using the helper
    const visibleRoleOptions = getVisibleRoleFilterOptions(isSenseUserOrImpersonated)

    const columns = [
        {
            title: 'Name',
            key: 'name',
            dataIndex: 'name',
            sorter: (a, b) => a.name?.length - b.name?.length,
        },
        {
            title: 'Email',
            key: 'email',
            dataIndex: 'email',
        },
        ...(isSystemAdmin ? [{
            title: 'Organization',
            key: 'organization',
            dataIndex: 'organization',
            filters: [
                ...(organizations?.map(organization => ({
                    text: organization.name,
                    value: organization.id,
                })) || []),
            ],
            onFilter: (value, record) =>
                record.organization === organizations?.find(organization => organization.id === value)?.name,
            filterSearch: true,
        }] : []),
        {
            title: 'Roles',
            key: 'roles',
            dataIndex: 'roles',
            render: (roles, record) => {
                const isTargetUserFromSense = record.email?.endsWith('@sense.network')
                const visibleRoles = getVisibleRoles(roles, isSenseUserOrImpersonated, isTargetUserFromSense)
                return visibleRoles.map(role => getRoleName(role)).join(', ')
            },
            filters: visibleRoleOptions,
            onFilter: (value, record) => {
                const isTargetUserFromSense = record.email?.endsWith('@sense.network')
                const visibleRoles = getVisibleRoles(record.roles, isSenseUserOrImpersonated, isTargetUserFromSense)
                return visibleRoles.includes(value)
            },
        },
        ...(isSystemAdmin ? [{
            title: 'Type',
            key: 'type',
            dataIndex: 'type',
            render: type => getTypeName(type),
            filters: Object.values(UserTypes).map(type => ({
                text: getTypeName(type),
                value: type,
            })),
            onFilter: (value, record) => record.type === value,
        }] : []),
        {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            render: status => {
                switch(status) {
                    case UserStatus.ACTIVE:
                        return <Tag icon={<CheckCircleOutlined />} color='success'>Active</Tag>;
                    case UserStatus.DISABLED:
                        return <Tag icon={<MinusCircleOutlined />} color='default'>Disabled</Tag>;
                    case UserStatus.PENDING:
                        return <Tag icon={<ClockCircleOutlined />} color='warning'>Pending</Tag>;
                    default:
                        return <Tag color='default'>{getUserStatusText(status)}</Tag>;
                }
            },
            filters: [
                { text: 'Active', value: UserStatus.ACTIVE },
                { text: 'Disabled', value: UserStatus.DISABLED },
                { text: 'Pending', value: UserStatus.PENDING }
            ],
            onFilter: (value, record) => record.status === value,
            sorter: (a, b) => a.status - b.status,
        },
        {
            title: 'Actions',
            key: 'actions',
            dataIndex: 'actions',
            render: (_, record) => (
                <Button
                    type='text'
                    icon={<EditOutlined />}
                    shape='circle'
                    title='Edit user'
                    className='primary-color'
                    onClick={() => dispatch(userActions.setEditingUserId(record.key))}
                />
            ),
        },
    ]

    let adaptedData =
        data?.map(user => {
            let organizationName = 'Unknown organization'
            if (isFetchingOrganizations) organizationName = '...'
            else if (organizations) {
                const organization = organizations.find(organization => organization.id === user.organizationId)
                if (organization) organizationName = organization.name
            } else if (isNil(user.organizationId)) organizationName = 'No organization'

            // Map legacy enabled property to status if status is not present
            const status = user.status || (user.enabled ? UserStatus.ACTIVE : UserStatus.DISABLED);

            return {
                key: user.id,
                name: user.name,
                email: user.email,
                roles: user.roles,
                type: user.type,
                organization: organizationName,
                status: status,
            }
        }) || []

    if (searchParam)
        adaptedData = adaptedData.filter(
            user =>
                user.name?.toLowerCase()?.includes(searchParam.toLowerCase()) ||
                user.email?.toLowerCase()?.includes(searchParam.toLowerCase()),
        )

    return error ? (
        <Result
            status='error'
            title='Users fetch failed'
            subTitle={error}
            extra={[
                <Button key='try' onClick={() => refetch()} loading={isFetching}>
                    Try Again
                </Button>,
            ]}
        />
    ) : (
        <Table
            columns={columns}
            dataSource={adaptedData}
            pagination={false}
            loading={isFetching}
            scroll={{ x: true }}
        />
    )
}

export default UsersTable
